import createAsyncGetters, {
  ElasticSearchQueryWhere,
} from 'src/replicant/config/asyncGetters/createAsyncGetters';
import { LeagueLeaderboardEntry, leaderboardSize } from './leagues.ruleset';
import { getMastersLeagueDifficultyBucketForLeague } from './leagues.getters';

export const leaguesAsyncGetters = createAsyncGetters({
  /**
   * Get league scoreboard. Includes player rank.
   * @param opts
   * @param api
   * @returns
   */
  getLeagueScoreboard: async (
    { leagueId, friendsOnly }: { leagueId: string; friendsOnly: boolean },
    api,
  ) => {
    const state = await api.getOwnState();

    const where: ElasticSearchQueryWhere = {
      id: {
        isNotOneOf: [state.id],
      },
      leagues: {
        leagueId: {
          isAnyOf: [leagueId],
        },
        isV2: {
          is: false,
        },
        // Keep historical global ranks within the original difficulty cohort.
        ...(!friendsOnly && {
          mastersLeagueDifficultyBucket: {
            isAnyOf: [
              getMastersLeagueDifficultyBucketForLeague(state, leagueId),
            ],
          },
        }),
      },
      // Friends leaderboard.
      ...(friendsOnly && {
        friendIds: {
          containsAnyOf: [state.id],
        },
      }),
    };

    // Get scores.
    const scores = await api.searchPlayers({
      where,
      sort: [
        {
          field: 'leagues.score',
          order: 'desc',
        },
        {
          field: 'leagues.modifiedAt',
          order: 'asc',
        },
      ],
      limit: leaderboardSize,
    });

    // Create leaderboard.
    const leaderboard: LeagueLeaderboardEntry[] = [];
    let playerRank = leaderboardSize + 1;
    scores.results.forEach(
      ({ id, leagues, publicProfileName, publicProfilePhoto }, index) => {
        const rank = index + 1;

        const entry = leagues.find((x) => x.leagueId === leagueId);
        leaderboard.push({
          id,
          rank,
          score: entry?.score ?? 0,
          name: publicProfileName,
          photo: publicProfilePhoto,
          modifiedAt: entry?.modifiedAt ?? 0,
        });
      },
    );

    // Include up to date player score, instead of indexed score.
    const player: LeagueLeaderboardEntry = {
      id: state.id,
      rank: -1,
      score: state.match2.leagues[leagueId]?.score ?? 0,
      modifiedAt: state.match2.leagues[leagueId]?.modifiedAt ?? 0,
      name: state.publicProfile?.name ?? '',
      photo: state.publicProfile?.photo ?? '',
      me: true,
    };

    // Find index to insert player.
    const playerIndex = leaderboard.findIndex(
      (x) =>
        player.score > x.score ||
        (player.score === x.score && player.modifiedAt < x.modifiedAt),
    );

    // Insert player in the correct position.
    leaderboard.splice(
      playerIndex === -1 ? leaderboard.length : playerIndex,
      0,
      player,
    );
    leaderboard.length = Math.min(leaderboard.length, leaderboardSize);

    // Update all ranks.
    leaderboard.forEach((x, index) => {
      if (x.id === state.id) {
        playerRank = index + 1;
      }

      x.rank = index + 1;
    });

    return { leaderboard, playerRank };
  },
});
